import { invoke } from "@tauri-apps/api/core";
import { IS_WEB } from "./api";

const CLOUD_BASE = "https://api.tryzwork.app";
/** Where Stripe sends a desktop user's browser back to once they're done:
 *  the web app shows a "return to zWork" page for these (App.tsx). */
const WEB_APP_BASE = "https://app.tryzwork.app";
const IS_TAURI = typeof window !== "undefined" && !!(window as any).__TAURI_INTERNALS__;
const TOKEN_KEY = "zwork:cloud-token";
const AUTH_CHANGED_EVENT = "zwork:cloud-auth-changed";

/** True when Google sign-in must use the web OAuth flow instead of the
 *  desktop `invoke("begin_desktop_auth")` path (which crashes in a browser:
 *  "Cannot read properties of undefined (reading 'invoke')").
 *
 *  Known web origins are treated as definitively web — `IS_WEB` keys off
 *  `__TAURI_INTERNALS__`, which a browser extension or stray global can make
 *  truthy, routing a web user into the desktop invoke() path. The Tauri shell
 *  never serves over https to a real hostname, so a spoofed global can't force
 *  the desktop code path. */
export function isWebAuthClient(): boolean {
  if (typeof window === "undefined") return false;
  const origin = window.location.origin;
  if (
    origin === "https://app.tryzwork.app" ||
    origin === "https://tryzwork.app" ||
    origin === "https://www.tryzwork.app"
  ) {
    return true;
  }
  return IS_WEB;
}

/** Start Better Auth Google social sign-in for web (non-Tauri) environments.
 *
 *  Navigates the browser (top-level) to /api/auth/web/google on the auth host.
 *  Better Auth mints the OAuth state, sets the state cookie, and 302s to Google
 *  — all in a first-party context on api.tryzwork.app. This avoids the
 *  cross-origin fetch + SameSite cookie storage issues that broke a fetch-based
 *  start (the state cookie set via a cross-origin XHR was not reliably stored,
 *  causing state_mismatch at callback time). On callback completion Better Auth
 *  sets the session cookie and redirects to `callbackURL` (the app origin). */
export function startWebGoogleSignIn() {
  const callbackURL = window.location.origin;
  window.location.href = `${CLOUD_BASE}/api/auth/web/google?callbackURL=${encodeURIComponent(callbackURL)}`;
}

/** Check URL for a token param from OAuth callback and store it. Returns true if token was found. */
export function handleOAuthTokenCallback(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setToken(token);
      // Clean the URL
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.pathname);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

class CloudFetchError extends Error {
  status: number;

  constructor(status: number, statusText: string, body: string) {
    super(friendlyCloudError(status, statusText, body));
    this.name = "CloudFetchError";
    this.status = status;
  }
}

export interface CloudUser {
  user_id: string;
  email: string;
  name: string;
  tier: "free" | "pro" | "max";
  access_code?: string | null;
  coupon_code?: string | null;
  stripe_customer_id?: string | null;
  subscription_id?: string | null;
  subscription_status?: string | null;
  subscription_price_id?: string | null;
  subscription_current_period_end?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsDay {
  day: string;
  roots: number;
  continuations: number;
}

export interface ProviderOverview {
  provider_name: string;
  requests_7d: number;
  roots_7d: number;
  continuations_7d: number;
  total_tokens_7d: number;
  prompt_tokens_7d: number;
  completion_tokens_7d: number;
  last_model_id?: string | null;
  last_status?: number | null;
  last_observed_at?: string | null;
  requests_limit_day?: number | null;
  requests_remaining_day?: number | null;
  requests_reset_day_seconds?: number | null;
  tokens_limit_minute?: number | null;
  tokens_remaining_minute?: number | null;
  tokens_reset_minute_seconds?: number | null;
}

export interface AnalyticsSummary {
  user: CloudUser;
  router_label: string;
  root_requests_today: number;
  continuation_requests_today: number;
  active_runs: number;
  root_requests_total: number;
  continuation_requests_total: number;
  five_hour_limit: number;
  five_hour_used: number;
  weekly_limit: number;
  weekly_used: number;
  past_week: AnalyticsDay[];
  past_month: AnalyticsDay[];
  /** Daily activity for the last 53 weeks, oldest first (the heatmap). */
  past_year?: AnalyticsDay[];
  /**
   * Free plan only: tasks zWork may still hand to its stronger model in the
   * rolling 30 days.
   */
  free_escalations_left?: number | null;
  free_escalations_limit?: number;
  managed_gateway_ready: boolean;
  managed_gateway_status: string;
  billing_enabled: boolean;
  billing_status: string;
  owner_provider_overview: ProviderOverview[];
  api_url: string;
  analytics_url: string;
  db_url: string;
}

export interface BillingSession {
  url: string;
}

function friendlyCloudError(status: number, statusText: string, body: string) {
  const message = body.trim();
  const normalized = message.replace(/^"+|"+$/g, "");
  const mapped =
    normalized === "missing_access_code"
      ? "Enter an access code first."
      : normalized === "email_and_password_required"
        ? "Enter both your email and password."
        : normalized === "name_email_password_required"
          ? "Enter your name, email, and password."
          : normalized.includes("verify your email")
            ? "Verify your email before signing in."
      : normalized === "invalid_access_code"
        ? "That access code is not valid on this server."
        : normalized === "not_signed_in"
          ? "Your session expired. Sign in again."
          : normalized === "access_denied"
            ? "Your account does not have access to this route yet."
            : normalized === "user_lookup_failed"
              ? "The server could not load your account. Try signing in again."
              : normalized === "access_code_update_failed"
                ? "The server could not apply this access code right now."
                : normalized === "root_request_quota_exceeded"
                  ? "You've used this period's allowance. It refills over the next few hours; upgrading raises it."
                  : normalized === "model_requires_pro_tier"
                    ? "That model needs the Pro plan."
                  : normalized === "model_requires_max_tier"
                    ? "That model needs the Max plan."
                  : normalized === "too_many_active_runs"
                    ? "You already have too many active runs. Wait for one to finish before starting another."
                    : normalized === "hosted_gateway_not_configured"
                      ? "zWork Router is not configured on the server right now."
                      : normalized === "stripe_billing_not_configured"
                        ? "Stripe billing is not configured on this server yet."
                        : normalized === "stripe_price_not_configured"
                          ? "The Pro Stripe price is not configured on this server."
                      : normalized.startsWith("router_upstreams_failed:")
                        ? "zWork Router could not get a healthy response from any upstream provider."
                        : normalized;
  if (mapped) return `${status} ${statusText}: ${mapped}`;
  return `${status} ${statusText}`;
}

function getToken() {
  return window.localStorage.getItem(TOKEN_KEY) || "";
}

export function getCloudToken() {
  return getToken();
}

function setToken(token: string) {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
}

export function clearCloudToken() {
  // Only fire the auth-changed event if there was actually a token to clear.
  // Otherwise every 401 (e.g. "not signed in yet" on the web) would re-trigger
  // session fetches and loop.
  const had = !!window.localStorage.getItem(TOKEN_KEY);
  window.localStorage.removeItem(TOKEN_KEY);
  if (had) {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  }
}

export function onCloudAuthChanged(listener: () => void) {
  window.addEventListener(AUTH_CHANGED_EVENT, listener);
  return () => {
    window.removeEventListener(AUTH_CHANGED_EVENT, listener);
  };
}

async function cloudFetch<T>(path: string, init?: RequestInit, token = getToken()): Promise<T> {
  const headers = new Headers(init?.headers || {});
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init?.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);
  const response = await fetch(`${CLOUD_BASE}${path}`, {
    ...init,
    headers,
    // Web (non-Tauri) requests are cross-origin (app.tryzwork.app →
    // api.tryzwork.app); include credentials so the Better Auth session cookie
    // travels alongside the Bearer token. Tauri requests ignore this.
    credentials: IS_TAURI ? "same-origin" : "include",
    signal: controller.signal,
  }).finally(() => clearTimeout(timeout));
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new CloudFetchError(response.status, response.statusText, text);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function startDesktopGoogleSignIn(): Promise<CloudUser> {
  const code = await invoke<string>("begin_desktop_auth", {
    startUrl: `${CLOUD_BASE}/api/desktop/auth/start`,
  });
  const result = await cloudFetch<{ token: string; user: CloudUser }>("/api/desktop/auth/exchange", {
    method: "POST",
    body: JSON.stringify({ code }),
  }, "");
  setToken(result.token);
  return result.user;
}

export async function startDesktopEmailSignIn(email: string, password: string): Promise<CloudUser> {
  const result = await cloudFetch<{ token: string; user: CloudUser }>("/api/desktop/auth/email/sign-in", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  }, "");
  setToken(result.token);
  return result.user;
}

export async function startDesktopEmailSignUp(name: string, email: string, password: string, callbackUrl?: string) {
  return cloudFetch<{ ok: boolean; verification_required: boolean; message: string }>(
    "/api/desktop/auth/email/sign-up",
    {
      method: "POST",
      body: JSON.stringify({
        name,
        email,
        password,
        callback_url: callbackUrl || `${window.location.origin}/auth/verified`,
      }),
    },
    "",
  );
}

export async function fetchCloudSession(): Promise<CloudUser | null> {
  const token = getToken();
  // On the web (non-Tauri), the session may be a Better Auth cookie set after
  // OAuth. There's no Bearer token in localStorage in that case, so ask
  // /api/session anyway: it authenticates via the cookie through
  // credentials: "include". The cookie is HttpOnly (and __Secure- prefixed in
  // production), so document.cookie can't tell us whether it's there.
  if (!token && IS_TAURI) return null;
  try {
    return await cloudFetch<CloudUser>("/api/session", undefined, token || "");
  } catch (error) {
    if (error instanceof CloudFetchError && (error.status === 401 || error.status === 403)) {
      // Only clear + notify when we actually held a token. On the web with only
      // a cookie, a 401 just means "not signed in" — clearing would fire the
      // auth-changed event and re-trigger the fetch in an infinite loop.
      if (token) clearCloudToken();
    }
    return null;
  }
}

export async function logoutCloudSession() {
  const token = getToken();
  if (!token) return;
  try {
    await cloudFetch("/api/desktop/auth/logout", { method: "POST" });
  } finally {
    clearCloudToken();
  }
}

export async function redeemAccessCode(code: string) {
  return cloudFetch<CloudUser>("/api/dev/redeem-coupon", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function fetchAnalyticsSummary() {
  return cloudFetch<AnalyticsSummary>("/api/analytics/summary");
}

export async function createBillingCheckoutSession(annual = false, tier = "pro") {
  const successUrl = IS_TAURI
    ? `${WEB_APP_BASE}/billing/success?from=desktop`
    : `${window.location.origin}/billing/success`;
  const cancelUrl = IS_TAURI
    ? `${WEB_APP_BASE}/billing/cancel?from=desktop`
    : `${window.location.origin}/billing/cancel`;
  return cloudFetch<BillingSession>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      annual,
      tier,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });
}

export async function createBillingPortalSession() {
  const returnUrl = IS_TAURI
    ? `${WEB_APP_BASE}/billing/portal-return?from=desktop`
    : `${window.location.origin}/billing/portal-return`;
  return cloudFetch<BillingSession>("/api/billing/portal", {
    method: "POST",
    body: JSON.stringify({
      return_url: returnUrl,
    }),
  });
}

/** Poll cloud session until the user's plan changes (or timeout). */
export function pollForPlanChange(
  currentTier: string,
  onPlanChanged: (user: CloudUser) => void,
  timeoutMs = 300_000,
): () => void {
  let stopped = false;
  const interval = setInterval(async () => {
    if (stopped) return;
    try {
      const user = await fetchCloudSession();
      if (user && user.tier !== currentTier) {
        stopped = true;
        clearInterval(interval);
        onPlanChanged(user);
      }
    } catch { /* ignore transient errors */ }
  }, 3_000);
  const timer = setTimeout(() => {
    stopped = true;
    clearInterval(interval);
  }, timeoutMs);
  return () => {
    stopped = true;
    clearInterval(interval);
    clearTimeout(timer);
  };
}

